import { useGetBeamerChatEnabled } from 'gcs-common/clients/api/entities/current-user/current-user-api-selectors';
import { BEAMER_CHANNEL_ID } from 'gcs-common/constants/channelConstants';
import {
  getChannelImage,
  getChannelMemberCount,
  getChannelStatus,
  getIsSalesSpaceChannelType,
  getIsSelectedChannel,
  getNumOpenTasksForChannel,
  getOtherMemberTyping,
} from 'gcs-common/slices/channel/channelSelectors';
import changeSelectedChannelId from 'gcs-common/slices/channels/channelsThunks/changeSelectedChannelId';
import { getUnreadMessageCountForChannel } from 'gcs-common/slices/channels/unreadMessagesSelectors';
import { getIsAgent } from 'gcs-common/slices/currentUser/currentUserSelectors';
import { getFavicon } from 'gcs-common/slices/theme/themeSelectors';
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import typingIcon from '../../img/edit_even_lighter.svg';
import AnimatedSidebarButton from '../AnimatedSidebarButton/AnimatedSidebarButton';
import ImageWithStatus from '../ImageWithStatus/ImageWithStatus';
import UnreadCountDisplay from '../UnreadCountDisplay/UnreadCountDisplay';
import styles from './styles.module.scss';

const getChannelStyle = ({ isSelectedChannel, isAgent }: { isSelectedChannel: boolean; isAgent: boolean }) => {
  const mainStyle = isAgent ? styles.channelListItem : styles.craftsmanChannelListItem;
  let selectedStyle = isAgent ? styles.unselectedChannelListItem : styles.unselectedCraftsmanChannelListItem;
  if (isSelectedChannel) {
    selectedStyle = isAgent ? styles.selectedChannelListItem : styles.selectedCraftsmanChannelListItem;
  }

  return `${mainStyle} ${selectedStyle}`;
};

interface ChannelListItemCollapsedProps {
  channelId: string;
}

const ChannelListItemCollapsed = ({ channelId }: ChannelListItemCollapsedProps) => {
  const isAgent = useSelector(getIsAgent);
  const isSelectedChannel = useSelector(getIsSelectedChannel(channelId));
  const numOpenTasks = useSelector(getNumOpenTasksForChannel(channelId));
  const unreadCount = useSelector(getUnreadMessageCountForChannel(channelId));
  const otherUserTyping = useSelector(getOtherMemberTyping(channelId));
  const channelMemberCount = useSelector(getChannelMemberCount(channelId));
  const isSalesSpace = useSelector(getIsSalesSpaceChannelType(channelId));
  const channelImage = useSelector(getChannelImage(channelId));
  const channelStatus = useSelector(getChannelStatus(channelId));
  const logo = useSelector(getFavicon);
  const isBeamerChatEnabled = useGetBeamerChatEnabled();

  const channelStyle = useMemo(() => {
    return getChannelStyle({ isSelectedChannel, isAgent });
  }, [isAgent, isSelectedChannel]);

  const dispatch = useDispatch();
  const selectChannel = useCallback(() => {
    dispatch(changeSelectedChannelId({ selectedChannelId: channelId }));
  }, [channelId, dispatch]);

  if (channelId === BEAMER_CHANNEL_ID) {
    if (!isBeamerChatEnabled) return null;
    return (
      <AnimatedSidebarButton onClick={selectChannel} className={channelStyle}>
        <ImageWithStatus imageUrl={logo} isSalesSpace isAgent={false} className={styles.channelImage} />
        {unreadCount > 0 && (
          <div className={styles.unreadMsgIndicator}>
            <p>
              <UnreadCountDisplay unreadCount={unreadCount} />
            </p>
          </div>
        )}
      </AnimatedSidebarButton>
    );
  }

  return (
    <AnimatedSidebarButton onClick={selectChannel} className={channelStyle}>
      <ImageWithStatus
        imageUrl={channelImage}
        userStatus={channelStatus}
        channelMemberCount={channelMemberCount}
        isSalesSpace={isSalesSpace}
        isAgent={isAgent}
        className={otherUserTyping ? styles.channelImageTyping : styles.channelImage}
      />
      {otherUserTyping && <img className={styles.userTyping} alt="user-typing" src={typingIcon} />}
      {unreadCount > 0 && (
        <div className={styles.unreadMsgIndicator}>
          <p>
            <UnreadCountDisplay unreadCount={unreadCount} />
          </p>
        </div>
      )}
      {numOpenTasks > 0 && (
        <div className={styles.todoIndicator}>
          <p>{numOpenTasks}</p>
        </div>
      )}
    </AnimatedSidebarButton>
  );
};

export default ChannelListItemCollapsed;
